import React from 'react';
import { useARGuide } from '../../contexts/ARGuideContext';
import { SVGElementRenderer } from './SVGElementRenderer';
import { GuideControls } from './GuideControls';
import { PausedMessage } from './PausedMessage';
import { useLocation, useNavigate } from 'react-router-dom';
import { getElementPosition } from '../../services/arGuide/ElementPositionTracker';
import { GuideElement, ArrowElement, HighlightElement, LabelElement, ElementPosition, GuideStep } from '../../types/arGuides';
import { ABRIR_SIDEBAR_EVENT } from './events';

// Prefijo de guideId de las guías del tutorial de onboarding (KAN-232).
// Mantener sincronizado con PREFIX en components/tutorial/TutorialContext.tsx.
// Se usa para EXCLUIR esas guías de la navegación automática de abajo: su
// runner (TutorialContext) ya navega por su cuenta y duplicaría el navigate.
// Decisión de diseño: detectar el origen por el prefijo del guideId en vez de
// agregar un campo `_origin` al tipo ARGuide — cero cambios en tipos ni en
// los productores de guías (TutorialContext / ChatContext), y es la misma
// técnica que ya usa el propio TutorialContext para reconocer sus guías.
const TUTORIAL_GUIDE_PREFIX = 'tutorial:';

// ¿El selector resuelve en algo VISIBLE de la ruta actual? Debe existir en el
// DOM, tener tamaño (display:none / gemelo oculto devuelven 0×0) y caer dentro
// del ancho del viewport. Lo último atrapa el drawer mobile cerrado: el aside
// existe pero está trasladado fuera de pantalla (-translate-x-full), así que
// sus anchors existen sin verse.
const selectorResoluble = (sel: string): boolean => {
  try {
    const el = document.querySelector(sel);
    if (!el) return false;
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return false;
    return r.right > 0 && r.left < window.innerWidth;
  } catch {
    return false; // selector inválido: mejor no decidir navegación sobre él
  }
};

// Extrae los selectores [data-ar-guide='...'] que el paso necesita que existan en
// el DOM para poder pintar sus highlight/arrow/label. Se usa para el polling de
// reintento (elementos que aparecen tarde, ej. botón de la página destino).
function collectStepSelectors(step: GuideStep | undefined): string[] {
  if (!step?.elements) return [];
  const selectors: string[] = [];
  for (const element of step.elements) {
    const el = element as GuideElement & { targetSelector?: string; position?: { near?: string } };
    if (el.type === 'highlight' || el.type === 'arrow') {
      if (typeof el.targetSelector === 'string') selectors.push(el.targetSelector);
    } else if (el.type === 'label') {
      const near = el.position?.near;
      if (typeof near === 'string' && near.startsWith('[')) selectors.push(near);
    }
  }
  return selectors;
}

export const ARGuideOverlay: React.FC = () => {
  const { activeGuide, currentStepIndex, isPaused, cancelGuide, completeStep, pauseGuide, resumeGuide, markStepCompletedByRoute } = useARGuide();
  const location = useLocation();
  const navigate = useNavigate();
  const currentStep = activeGuide?.steps[currentStepIndex];

  // State para forzar recálculo de posiciones con scroll
  const [scrollOffset, setScrollOffset] = React.useState(0);
  // Tick de polling: cuando un selector del paso actual aún no está en el DOM,
  // lo incrementamos periódicamente para forzar el recálculo del useMemo y así
  // pintar el highlight/arrow en cuanto el elemento aparezca (ej. botón de la
  // página destino que monta tarde). Evita la falla silenciosa de "solo texto".
  const [pollTick, setPollTick] = React.useState(0);

  // Enrich elements with positions - calcula dinámicamente en CADA render (no useMemo)
  // Esto permite que las posiciones se actualicen con scroll
  const enrichedElements = React.useMemo(() => {
    if (!currentStep?.elements) return [];

    const enrichElement = (element: GuideElement): GuideElement | null => {
      // Handle ArrowElement with selectors in from/to or simplified targetSelector format
      if (element.type === 'arrow') {
        const arrow = element as any;
        let from: ElementPosition | null = null;
        let to: ElementPosition | null = null;

        // Format 1: Standard ArrowElement with from/to
        if (arrow.from && arrow.to) {
          from = typeof arrow.from === 'object' && 'selector' in arrow.from
            ? getElementPosition(arrow.from.selector)
            : arrow.from;

          to = typeof arrow.to === 'object' && 'selector' in arrow.to
            ? getElementPosition(arrow.to.selector)
            : arrow.to;
        }
        // Format 2: Simplified format with targetSelector only
        else if (arrow.targetSelector) {
          const pos = getElementPosition(arrow.targetSelector);
          if (pos) {
            from = { x: window.innerWidth / 2, y: 50 + window.scrollY, width: 0, height: 0 };
            to = pos;
          }
        }

        if (from && to && typeof from === 'object' && 'x' in from && typeof to === 'object' && 'x' in to) {
          return {
            type: 'arrow',
            from,
            to,
            animation: arrow.animation || 'draw'
          } as ArrowElement;
        }
        return null;
      }

      // Handle HighlightElement with targetSelector or position
      if (element.type === 'highlight') {
        const highlight = element as any;
        let position: ElementPosition | null = null;

        if (highlight.position && highlight.position.width > 0 && highlight.position.height > 0) {
          position = highlight.position;
        } else if (highlight.targetSelector) {
          position = getElementPosition(highlight.targetSelector);
        }

        if (position) {
          return {
            type: 'highlight',
            position,
            shape: highlight.shape || 'rectangle',
            animation: highlight.animation || 'pulse',
            infinite: highlight.infinite || false
          } as HighlightElement;
        }
        return null;
      }

      // Handle LabelElement with position.near
      if (element.type === 'label') {
        const label = element as LabelElement;

        if (typeof label.position === 'object' && 'x' in label.position) {
          return element;
        }

        if (typeof label.position === 'object' && 'near' in label.position) {
          const domPosition = getElementPosition(label.position.near);
          if (domPosition) {
            return {
              ...label,
              position: domPosition
            } as LabelElement;
          }
        }

        return null;
      }

      return element;
    };

    return currentStep.elements
      .map(enrichElement)
      .filter((e): e is GuideElement => e !== null);
  }, [currentStep, scrollOffset, pollTick]); // currentStep + scrollOffset + pollTick (reintento de elemento)

  // Recalcular posiciones cuando hay scroll
  React.useEffect(() => {
    if (!activeGuide || isPaused) return;

    // scrollOffset es solo un trigger de re-render; los valores reales se
    // recalculan en el useMemo de enrichedElements vía getElementPosition.
    // Antes esto sumaba scrollY + scrollX (sin sentido). Usamos scrollY como
    // señal de cambio, que es lo que importa para disparar el recálculo.
    const handleScroll = () => {
      setScrollOffset(window.scrollY);
    };

    // Usar requestAnimationFrame para optimizar performance
    let ticking = false;
    const optimizedScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    // scroll con CAPTURA: el evento de scroll no burbujea, y en desktop esta
    // app scrollea dentro del <main> (Layout md:h-screen + overflow-y-auto),
    // no en window. Sin captura, scrollear el contenido con un paso visible
    // dejaba el box clavado en la posición vieja.
    window.addEventListener('scroll', optimizedScroll, { passive: true, capture: true });
    window.addEventListener('resize', optimizedScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', optimizedScroll, { capture: true });
      window.removeEventListener('resize', optimizedScroll);
    };
  }, [activeGuide, isPaused, currentStep]);

  // Vigilancia de posiciones (reemplaza al poll de 3s): mientras el paso tiene
  // selectores, cada 400ms se re-lee su rect y se compara con la última firma
  // conocida. Si ALGO cambió — el elemento apareció tarde, se movió por reflow
  // (el mapa cambia de alto al asentarse, una tabla aparece encima), o el
  // usuario abrió el menú ☰ del celular exponiendo el ancla — se fuerza el
  // recálculo. Sin esto el highlight quedaba dibujado en una posición vieja.
  React.useEffect(() => {
    if (!activeGuide || !currentStep || isPaused) return;
    const selectors = collectStepSelectors(currentStep);
    if (selectors.length === 0) return;

    const firma = (sel: string): string => {
      const el = document.querySelector(sel);
      if (!el) return 'missing';
      const r = el.getBoundingClientRect();
      return `${Math.round(r.left)},${Math.round(r.top)},${Math.round(r.width)},${Math.round(r.height)}`;
    };
    const firmasActuales = () => selectors.map(firma).join('|');
    let ultima = firmasActuales();

    const interval = setInterval(() => {
      const ahora = firmasActuales();
      if (ahora !== ultima) {
        ultima = ahora;
        setPollTick(t => t + 1);
      }
    }, 400);

    return () => clearInterval(interval);
  }, [activeGuide, currentStep, isPaused]);

  // Auto-scroll: al entrar a un paso (o cuando el vigilante detecta que un
  // selector que faltaba apareció), si el primer selector resoluble del paso
  // queda fuera del viewport, se lo trae al centro. scrollIntoView resuelve
  // solo la cadena de contenedores scrolleables (el nav del sidebar y el
  // <main> scrollean por separado). Una vez por paso+selector: si el usuario
  // se va a mirar otra cosa a propósito, no lo persiga.
  const lastAutoScrollRef = React.useRef<string>('');
  React.useEffect(() => {
    if (!activeGuide || !currentStep || isPaused) return;
    const selectors = collectStepSelectors(currentStep);
    for (const sel of selectors) {
      const el = document.querySelector(sel) as HTMLElement | null;
      if (!el) continue;
      const r = el.getBoundingClientRect();
      // Gemelo oculto (display:none) u oculto real (drawer cerrado): sin scroll.
      if (r.width === 0 && r.height === 0) continue;
      if (r.right < 0) break;
      // Ya visible (con margen de tolerancia): nada que hacer.
      if (r.top >= -24 && r.bottom <= window.innerHeight + 24) break;
      const key = `${currentStep.stepId}:${sel}`;
      if (lastAutoScrollRef.current === key) break;
      lastAutoScrollRef.current = key;
      el.scrollIntoView({ block: 'center', behavior: 'smooth', inline: 'nearest' });
      break;
    }
  }, [activeGuide, currentStep, isPaused, pollTick]);

  // Avance por APARICIÓN de elemento (advanceWhenVisible): pasos que avanzan
  // solos cuando cierto selector entra al DOM (ej: se abre el diálogo de
  // valores nutricionales al elegir un alimento). Poll liviano cada 400 ms,
  // corta en cuanto aparece.
  React.useEffect(() => {
    if (!activeGuide || !currentStep || isPaused) return;
    const selector = (currentStep as GuideStep & { advanceWhenVisible?: string }).advanceWhenVisible;
    if (!selector) return;
    const interval = setInterval(() => {
      if (document.querySelector(selector)) {
        clearInterval(interval);
        completeStep();
      }
    }, 400);
    return () => clearInterval(interval);
  }, [activeGuide, currentStep, isPaused, completeStep]);

  // ---- Navegación automática (guías originadas en el CHAT) ----
  // Causa raíz auditada (2026-09-29): las guías golden/LLM no llevaban al
  // usuario a la pantalla del paso — el overlay quedaba en "solo texto"
  // hasta que el usuario adivinara dónde ir. Si el paso declara expectedRoute
  // y NINGÚN selector señalado resuelve en la vista actual, navegamos a esa
  // ruta una única vez por paso (guard anti-loop: si el usuario vuelve
  // manualmente a la página vieja, respetamos su elección).
  // La condición de "ninguno resuelve" es crítica: muchos pasos 1 señalan
  // chrome global (sidebar, launcher del chat) presente en toda la app aunque
  // declaren expectedRoute — si algo del paso se ve donde estás, no
  // teletransportamos. El polling existente (pollTick) re-renderiza el
  // highlight cuando los elementos de la página destino aparecen.
  const ultimaNavPorPasoRef = React.useRef<string>('');
  const ultimoDrawerPorPasoRef = React.useRef<string>('');
  React.useEffect(() => {
    if (!activeGuide || activeGuide.status !== 'active' || !currentStep || isPaused) return;
    if (activeGuide.guideId.startsWith(TUTORIAL_GUIDE_PREFIX)) return;

    const selectors = collectStepSelectors(currentStep);
    if (selectors.some(selectorResoluble)) return;

    const expected = currentStep.expectedRoute?.split('?')[0];
    const pasoKey = `${activeGuide.guideId}:${currentStep.stepId}`;

    // Caso 1: hay ruta esperada y no estamos en ella → navegar (una vez).
    if (expected && location.pathname !== expected) {
      if (ultimaNavPorPasoRef.current === pasoKey) return;
      ultimaNavPorPasoRef.current = pasoKey;
      navigate(expected);
      return;
    }

    // Caso 2: estamos en la ruta correcta (o el paso no declara una) y todos
    // los selectores EXISTEN pero quedan totalmente a la izquierda del
    // viewport → viven en el sidebar, ocultos por el drawer mobile cerrado.
    // Pedirle al Layout que lo abra (una vez por paso; en desktop el aside
    // está siempre visible y el patrón no ocurre).
    if (
      selectors.length > 0 &&
      selectors.every(sel => {
        try {
          const el = document.querySelector(sel);
          if (!el) return false;
          const r = el.getBoundingClientRect();
          return r.width > 0 && r.height > 0 && r.right <= 0;
        } catch {
          return false;
        }
      }) &&
      ultimoDrawerPorPasoRef.current !== pasoKey
    ) {
      ultimoDrawerPorPasoRef.current = pasoKey;
      window.dispatchEvent(new CustomEvent(ABRIR_SIDEBAR_EVENT));
    }
  }, [activeGuide, currentStep, isPaused, location.pathname, navigate, pollTick]);

  // ---- Pausa automática (guías del chat, pasos SIN expectedRoute) ----
  // Antes: un paso cuyo objetivo no estaba en la ruta actual mostraba texto
  // mudo sin highlights ni salida. Ahora, si tras una gracia (los elementos
  // pueden montar tarde — el vigilante de posiciones sigue reintentando) ningún
  // selector resuelve, la guía se pausa y el PausedMessage ofrece "Saltar
  // paso"/"Cancelar guía". Si el paso no señala nada (solo texto), no hay con
  // qué decidir ubicación: no pausamos. Una vez pausada, vigilamos la
  // aparición del selector para resumir solos (el usuario navegó a mano a la
  // pantalla correcta, o el drawer se abrió).
  React.useEffect(() => {
    if (!activeGuide || activeGuide.status !== 'active' || !currentStep) return;
    if (activeGuide.guideId.startsWith(TUTORIAL_GUIDE_PREFIX)) return;
    if (currentStep.expectedRoute) return; // con ruta esperada: navega el effect de arriba
    const selectors = collectStepSelectors(currentStep);
    if (selectors.length === 0) return;

    if (!isPaused) {
      const timer = setTimeout(() => {
        if (!selectors.some(selectorResoluble)) pauseGuide();
      }, 1500);
      return () => clearTimeout(timer);
    }

    const interval = setInterval(() => {
      if (selectors.some(selectorResoluble)) resumeGuide();
    }, 400);
    return () => clearInterval(interval);
  }, [activeGuide, currentStep, isPaused, pauseGuide, resumeGuide]);

  // Aviso direccional: mientras el target del paso quede fuera del viewport
  // (abajo/arriba), mostrar una pastilla que dice hacia dónde deslizar. Se
  // recalcula en el mismo ciclo que el highlight (scroll/resize/poll), así se
  // oculta sola apenas el usuario scrollea y el target entra en vista.
  const [targetOffscreen, setTargetOffscreen] = React.useState<'below' | 'above' | null>(null);
  React.useEffect(() => {
    if (!activeGuide || !currentStep || isPaused) {
      setTargetOffscreen(null);
      return;
    }
    let dir: 'below' | 'above' | null = null;
    for (const sel of collectStepSelectors(currentStep)) {
      const el = document.querySelector(sel);
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) continue;
      if (r.right < 0) continue;
      if (r.bottom > window.innerHeight + 24) { dir = 'below'; break; }
      if (r.top < -24) { dir = 'above'; break; }
    }
    setTargetOffscreen(dir);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGuide, currentStep, isPaused, scrollOffset, pollTick]);

  React.useEffect(() => {
    if (activeGuide && !isPaused) {
      markStepCompletedByRoute(location.pathname);
    }
  }, [location.pathname, activeGuide, isPaused, markStepCompletedByRoute]);

  // Detección automática de clics en elementos señalados
  React.useEffect(() => {
    if (!activeGuide || !currentStep || isPaused) return;

    // Ref local al effect para trackear el timer de avance y poder limpiarlo si
    // el overlay se desmonta (o el effect se re-corre) dentro de los 300ms,
    // evitando un completeStep() sobre un componente desmontado.
    let advanceTimer: ReturnType<typeof setTimeout> | null = null;

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target) return;

      // Pasos que no avanzan por clic (ej. campos de formulario del
      // tutorial: el usuario los llena y avanza con el botón ✓).
      if (currentStep.advanceOnClick === false) return;

      // Obtener todos los selectores del paso actual (los highlights
      // visualOnly sólo se dibujan: no cuentan para el avance por clic)
      const selectors = currentStep.elements
        ?.map((element: GuideElement) => {
          if (element.type === 'arrow') {
            const arrow = element as any;
            return arrow.targetSelector || arrow.to?.selector;
          }
          if (element.type === 'highlight') {
            const highlight = element as any;
            if (highlight.visualOnly) return null;
            return highlight.targetSelector || highlight.position?.selector;
          }
          return null;
        })
        .filter(Boolean);

      if (!selectors || selectors.length === 0) return;

      // Verificar si el clic ocurrió en alguno de los elementos señalados
      const clickedElement = selectors.some((selector: string) => {
        try {
          const element = target.closest(selector);
          return element !== null;
        } catch (e) {
          return false;
        }
      });

      if (clickedElement) {
        console.log('[ARGuide] Click detected on guided element, advancing step');
        // Pequeño delay para que la acción del clic se procese primero.
        // OJO: limpiar el timer pendiente — un doble clic en el botón (común
        // por la demora de 300ms) reasignaba sin limpiar y DOS timers
        // avanzaban dos pasos, salteándose el siguiente (ej: Guardar → Salir).
        if (advanceTimer) clearTimeout(advanceTimer);
        advanceTimer = setTimeout(() => completeStep(), 300);
      }
    };

    // Agregar listener global
    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
      if (advanceTimer) clearTimeout(advanceTimer);
    };
  }, [activeGuide, currentStep, isPaused, completeStep]);

  if (!activeGuide || !currentStep) return null;

  if (activeGuide.status === 'completed') {
    return (
      <div className="fixed inset-0 z-[55] flex items-center justify-center bg-ink/40 pointer-events-auto">
        <div className="bg-paper rounded-lg p-6 shadow-overlay text-center">
          <h2 className="text-2xl font-display font-bold text-forest mb-2">¡Guía completada!</h2>
          <p className="text-ink">Ya sabes cómo realizar esta tarea.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* SVG para los elementos visuales (flechas, highlights, etc.) */}
      <svg
        className="absolute top-0 left-0 w-full pointer-events-none z-[55]"
        style={{ height: Math.max(document.documentElement.scrollHeight, window.innerHeight) + 'px' }}
      >
        {enrichedElements.map((element, index) => (
          <SVGElementRenderer
            key={`${currentStep.stepId}-${index}`}
            element={element}
          />
        ))}
      </svg>

      {/* PausedMessage fuera del SVG - como fixed overlay DOM (no SVG foreignObject) */}
      {isPaused && (
        <PausedMessage
          // "Ir a la pantalla correcta": navega a la ruta esperada y despausa
          // (solo pasos con expectedRoute — la pausa automática de pasos sin
          // ruta oculta este botón). El effect de route-change completará el
          // paso automáticamente cuando la ruta coincida (markStepCompletedByRoute
          // / el listener de clic).
          onResume={() => {
            if (currentStep?.expectedRoute) {
              navigate(currentStep.expectedRoute);
            }
            resumeGuide();
          }}
          onCancel={cancelGuide}
          // "Saltar este paso": avanza el paso ignorando el mismatch de ruta.
          onIgnore={() => {
            resumeGuide();
            completeStep();
          }}
          currentRoute={location.pathname}
          expectedRoute={currentStep?.expectedRoute}
        />
      )}

      {/* Aviso direccional: el ítem marcado está fuera de la vista */}
      {targetOffscreen && !isPaused && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[60] pointer-events-none">
          <div className="bg-forest-mid text-paper px-4 py-2 rounded-md shadow-lift text-sm font-medium animate-pulse">
            {targetOffscreen === 'below'
              ? '👇 El paso está más abajo — deslizá'
              : '👆 El paso está más arriba — deslizá'}
          </div>
        </div>
      )}

      {/* Controles fuera del SVG - más visibles y accesibles */}
      <GuideControls
        onClose={cancelGuide}
        onComplete={completeStep}
        disabled={isPaused}
        stepText={currentStep?.text}
        currentStep={currentStepIndex + 1}
        totalSteps={activeGuide?.steps?.length || 0}
        source={activeGuide?._source}
        audioUrl={currentStep?.audioUrl}
        // Paso informativo: no señala nada para clickear → botón "Siguiente".
        pasoInformativo={collectStepSelectors(currentStep).length === 0}
      />
    </>
  );
};
